import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '../../core/models/data';

export type RulesAccessProfile = {
  authUid?: string;
  appUserId?: string;
  canonicalUserId?: string;
  sourceUserDocId?: string;
  role?: User['role'];
  unitIds?: string[];
  accessProfileVersion?: number;
  disabled?: boolean;
  suspended?: boolean;
  authorityStatus?: string;
  bindingGeneration?: number;
  schedulerReadAuthority?: { authorityGeneration?: string; roleGeneration?: string; status?: string; units?: Record<string, { policyGeneration?: string }> };
};

// The request and its published projection have the same owner. A new owner
// masks retained state during render, before effect cleanup/refetch can run.
export function useAppAccessProfile({ currentUser, firebaseAuthUid, isDemoMode, load, subscribe, normalizeUnits }: {
  currentUser: (User & { active?: boolean; isActive?: boolean }) | null;
  firebaseAuthUid: string | null;
  isDemoMode: boolean;
  load: (uid: string) => Promise<RulesAccessProfile | null>;
  subscribe?: (uid: string, next: (profile: RulesAccessProfile | null) => void, error: () => void) => () => void;
  normalizeUnits: (value: unknown) => string[];
}) {
  const [epoch, setEpoch] = useState(0);
  const key = JSON.stringify([isDemoMode, firebaseAuthUid, currentUser?.id, currentUser?.authUid,
    currentUser?.accessProfileVersion ?? 0, currentUser?.role, currentUser?.active, currentUser?.isActive,
    currentUser ? normalizeUnits(currentUser).sort() : [], epoch]);
  const owner = useMemo(() => ({ key }), [key]);
  const liveOwner = useRef<typeof owner | null>(owner);
  liveOwner.current = owner;
  const [accepted, setAccepted] = useState<{
    owner: typeof owner; profile: RulesAccessProfile | null; blocked: boolean;
  } | null>(null);
  const resetAccessProfile = useCallback(() => {
    liveOwner.current = null;
    setAccepted(null);
    setEpoch(value => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    setAccepted(null);
    const publish = (profile: RulesAccessProfile | null, blocked: boolean) => {
      if (!cancelled && liveOwner.current === owner) setAccepted({ owner, profile, blocked });
    };
    if (isDemoMode) publish(null, false);
    else if (currentUser && firebaseAuthUid && currentUser.authUid === firebaseAuthUid && currentUser.active !== false && currentUser.isActive !== false) {
      const receive = (profile: RulesAccessProfile | null) => {
        // A freshly returned stored projection can itself be stale. It must
        // match the source which initiated this request, not replace it.
        const matches = !profile || (
          profile.authUid === firebaseAuthUid && profile.sourceUserDocId === currentUser.id &&
          (!profile.appUserId || profile.appUserId === currentUser.id) &&
          (!profile.canonicalUserId || profile.canonicalUserId === currentUser.id) &&
          (profile.accessProfileVersion ?? 0) === (currentUser.accessProfileVersion ?? 0) &&
          profile.role === currentUser.role &&
          JSON.stringify(normalizeUnits(profile).sort()) === JSON.stringify(normalizeUnits(currentUser).sort())
        );
        publish(matches ? profile : null, !matches || profile?.authorityStatus === 'denied' ||
          profile?.disabled === true || profile?.suspended === true);
      };
      if (subscribe) unsubscribe = subscribe(firebaseAuthUid, receive, () => publish(null, true));
      else void load(firebaseAuthUid).then(receive, () => publish(null, true));
    } else if (currentUser) publish(null, true);
    return () => { cancelled = true; unsubscribe?.(); };
  }, [owner, load, subscribe, isDemoMode, normalizeUnits]);

  const current = accepted?.owner === owner ? accepted : null;
  const accessProfile = current?.profile ?? null;
  const isAccessProfileLoaded = current !== null;
  const isAccessProfileBlocked = current?.blocked ?? false;
  // A confirmed absent legacy projection retains the existing P0 fallback.
  // Unresolved, failed or mismatched projections never activate that fallback.
  const authorizationUnitIds = useMemo(() => currentUser && current && !current.blocked
    ? normalizeUnits(current.profile ?? currentUser) : [], [currentUser, current, normalizeUnits]);
  const effectiveCurrentUser = useMemo(() => !currentUser || !current || current.blocked ? null
    : { ...currentUser, unitIds: authorizationUnitIds,
      bindingGeneration: current.profile?.bindingGeneration,
      authorityGeneration: current.profile?.schedulerReadAuthority?.authorityGeneration,
      schedulerAuthorityVersion: current.profile?.schedulerReadAuthority ? JSON.stringify(current.profile.schedulerReadAuthority) : undefined,
    }, [currentUser, current, authorizationUnitIds]);
  return { accessProfile, isAccessProfileLoaded, isAccessProfileBlocked, effectiveCurrentUser,
    authorizationUnitIds, resetAccessProfile };
}
